import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { adminDiagnosticsApi } from '@/api/adminDiagnostics'
import type { DiagnosticAssignmentRow, DiagnosticResultResponse } from '@/api/adminDiagnostics'
import type { DiagnosticStatus } from '@/api/learnerDiagnostics'

export const DIAGNOSTIC_RESULT_POLL_INTERVAL_MS = 5_000

export const diagnosticResultQueryKey = (assignmentId: string) =>
  ['diagnostic-result', assignmentId] as const

export function isDiagnosticTerminalStatus(status: DiagnosticStatus | undefined): boolean {
  return status === 'COMPLETED' || status === 'CANCELLED'
}

export function getDiagnosticPollInterval(status: DiagnosticStatus | undefined): number | false {
  return isDiagnosticTerminalStatus(status) ? false : DIAGNOSTIC_RESULT_POLL_INTERVAL_MS
}

export function mergeDiagnosticLiveResult(
  assignment: DiagnosticAssignmentRow | undefined,
  result: DiagnosticResultResponse | undefined,
): DiagnosticAssignmentRow | undefined {
  if (!assignment || !result) return assignment

  const aiqSolved = result.aiq.items.reduce(
    (count, item) => count + (item.isCorrect == null ? 0 : 1),
    0,
  )

  return {
    ...assignment,
    status: result.status,
    aiqSolved,
    aiqTotal: result.aiq.total,
    levelGraded: result.level != null,
  }
}

interface UseDiagnosticLiveResultOptions {
  assignment: DiagnosticAssignmentRow | undefined
  userId: number | undefined
  enabled: boolean
}

/**
 * 열린 진단 팝업의 대표 회차만 폴링한다.
 * 학생 목록 전체를 주기 조회하지 않고 기존 result API 응답을 row 상태로 병합한다.
 */
export function useDiagnosticLiveResult({
  assignment,
  userId,
  enabled,
}: UseDiagnosticLiveResultOptions) {
  const queryClient = useQueryClient()
  const assignmentId = assignment?.id
  const assignmentStatus = assignment?.status
  const previousStatusRef = useRef<{ assignmentId: string; status: DiagnosticStatus } | null>(null)

  const resultQuery = useQuery({
    queryKey: diagnosticResultQueryKey(assignmentId ?? ''),
    queryFn: () => adminDiagnosticsApi.getResult(assignmentId!),
    enabled: enabled && assignmentId != null && !isDiagnosticTerminalStatus(assignmentStatus),
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    refetchInterval: (query) => {
      const result = query.state.data?.data.data
      const status = result?.status ?? assignmentStatus
      return getDiagnosticPollInterval(status)
    },
  })

  const result = resultQuery.data?.data.data
  const serverStatus = result?.status

  useEffect(() => {
    if (!assignmentId || !assignmentStatus) {
      previousStatusRef.current = null
      return
    }

    const previousStatus =
      previousStatusRef.current?.assignmentId === assignmentId
        ? previousStatusRef.current.status
        : assignmentStatus
    const currentStatus = serverStatus ?? assignmentStatus

    previousStatusRef.current = { assignmentId, status: currentStatus }
    if (!serverStatus || previousStatus === currentStatus) return

    const invalidations = [
      queryClient.invalidateQueries({ queryKey: ['lc', 'students'] }),
      queryClient.invalidateQueries({ queryKey: ['diagnostic-report-summary', assignmentId] }),
    ]

    if (userId != null) {
      invalidations.push(
        queryClient.invalidateQueries({ queryKey: ['lc', 'admin', 'diagnosticReport', userId] }),
        queryClient.invalidateQueries({ queryKey: ['lc', 'admin', 'levelResult', userId] }),
        queryClient.invalidateQueries({ queryKey: ['aiqDiagnosticReport', userId] }),
      )
    }

    void Promise.all(invalidations)
  }, [assignmentId, assignmentStatus, queryClient, serverStatus, userId])

  return {
    ...resultQuery,
    result,
    assignment: mergeDiagnosticLiveResult(assignment, result),
  }
}
